import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import { Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize, takeUntil } from 'rxjs';
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from 'src/app/common/common';
import { RegisteredReportViewModel } from 'src/app/model/registeredReports.model';
import { AuthService } from 'src/app/services/auth.service';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {LoaderComponent} from "../../../common/loader/loader.component";
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: "app-registered-report-index",
  standalone: true,
  imports: [
    NgxPaginationModule,
    FormsModule,
    CommonModule,
    RouterModule,
    LoaderComponent
  ],
  templateUrl: "./registered-report-index.component.html",
  styleUrl: "./registered-report-index.component.scss",
})
export class RegisteredReportIndexComponent implements OnInit, OnDestroy {
  // Search fields (excluding # RIDs and Metadata Report ID as requested)
  public reportIdSearch: string = '';
  public reportRequestIdSearch: string = '';
  public venIdSearch: string = '';
  public granularitySearch: string = '';
  public reportBackDurationSearch: string = '';
  public reportIntervalStartSearch: string = '';
  public reportIntervalDurationSearch: string = '';
  public metadataReportSearch: string = '';

  // Debounce search using BehaviorSubject
  private searchParams$ = new BehaviorSubject<any>({
    reportId: '',
    reportRequestId: '',
    venId: '',
    metadataReportId: '',
    granularity: '',
    reportBackDuration: '',
    reportIntervalStart: '',
    reportIntervalDuration: ''
  });
  private unsubscribe = new Subject<void>();

  protected readonly paginationId = 'registeredReportsPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  public isAdmin: boolean = false;
  public isLoading: boolean = false;
  public error: string | null = null;
  public banner: string | null = null;
  public displayedRegisteredReports: RegisteredReportViewModel[] = [];
  private redirectTimeout: any;
  public router: Router;

  constructor(
    private injector: Injector,
    private authService: AuthService,
    private registeredReportsService: ApiService
  ) {
    this.router = injector.get(Router);
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search that tracks actual parameter changes
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.loadRegisteredReports();
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();

    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depths': {
        'metadata_report': 1,
        'rids': 1,
        'ven': 1
      },
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    // Add search parameters if they exist
    if (this.reportIdSearch) {
      params['id'] = this.reportIdSearch.trim();
    }
    if (this.reportRequestIdSearch) {
      params['report_request_id'] = this.reportRequestIdSearch.trim();
    }
    if (this.venIdSearch) {
      // Use nested parameter syntax for VEN ID since it's under the ven object
      params['ven.ven_id'] = this.venIdSearch.trim();
    }
    if (this.granularitySearch) {
      params['granularity'] = this.granularitySearch.trim();
    }
    if (this.metadataReportSearch) {
      params['metadata_report.report_specifier_id'] = this.metadataReportSearch.trim();
    }
    if (this.reportBackDurationSearch) {
      params['report_back_duration'] = this.reportBackDurationSearch.trim();
    }
    if (this.reportIntervalStartSearch) {
      params['report_interval_dtstart'] = this.reportIntervalStartSearch.trim();
    }
    if (this.reportIntervalDurationSearch) {
      params['report_interval_duration'] = this.reportIntervalDurationSearch.trim();
    }

    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();

    // Update the search parameters subject with current values
    this.searchParams$.next({
      reportId: this.reportIdSearch,
      reportRequestId: this.reportRequestIdSearch,
      venId: this.venIdSearch,
      metadataReportId: this.metadataReportSearch,
      granularity: this.granularitySearch,
      reportBackDuration: this.reportBackDurationSearch,
      reportIntervalStart: this.reportIntervalStartSearch,
      reportIntervalDuration: this.reportIntervalDurationSearch
    });
  }

  loadRegisteredReports() {
    this.isLoading = true;
    this.error = null;

    // Build parameters using the centralized method
    const queryParams = this.buildSearchParams();

    this.registeredReportsService.getRegisteredReports(queryParams)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.displayedRegisteredReports = response;
          this.paginationModel = this.pagination.updateTotalItems(response.length);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading registered reports:', err);
          this.error = extractErrorMessage(err, "Failed to load registered reports.");
          this.isLoading = false;
        }
      });
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.loadRegisteredReports();
  }

  nextPage(event: any, url: string, router: Router) {
    return goToNextPage(event, url, router);
  }

  delete(report: any) {
    if (confirm('Are you sure you want to delete this Registered Report?')) {
      this.banner = "Report cancellation has been sent to VTN. This may take a few minutes. " +
        "Check the registered report page to validate. You will be redirected to the registered report index after 20 seconds.";

      // Set a timeout to redirect after 20 seconds
      this.redirectTimeout = setTimeout(() => {
        if (this.redirectTimeout) {
          this.router.navigate(['/registeredReports']);
        }
      }, 20000);

      this.registeredReportsService
        .deleteRegisteredReports(report.id)
        .pipe(
          finalize(() => {
            // Clear the redirect timeout as we've completed the request
            if (this.redirectTimeout) {
              clearTimeout(this.redirectTimeout);
              this.redirectTimeout = null;
            }
          }),
          takeUntil(this.unsubscribe)
        )
        .subscribe({
          next: () => {
            this.banner = "Report successfully cancelled. Reloading registered reports...";

            // Set a timeout to reload the reports after 5 seconds
            setTimeout(() => {
              this.loadRegisteredReports();
              this.banner = null;
            }, 5000);
          },
          error: (err) => {
            console.error('Error deleting registered report:', err);
            this.error = extractErrorMessage(err, "Failed to cancel registered report.");
            this.banner = null;
          }
        });
    }
  }
}
